import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import Table from '../../UI/Table';
import { Button, IconButton } from '../../UI/Buttons';
import { IoCheckmarkCircle, IoCloseCircle, IoCreateOutline } from 'react-icons/io5';
import { Text } from '../../UI/Typography';

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  font-size: 13px;
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
`;

const TopicInput = styled.input`
  padding: 6px;
  width: 100%;
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 4px;
`;

const ActionButton = styled(IconButton)`
  margin-right: ${({ theme }) => theme.spacing.xs};
`;

function CorrectionsTable({ corrections = [], onUpdate }) {
  const [editing, setEditing] = useState({});

  const startEdit = useCallback((id, topic) => setEditing(prev => ({ ...prev, [id]: topic })), []);
  const cancelEdit = useCallback((id) => setEditing(prev => { const n = { ...prev }; delete n[id]; return n; }), []);

  const columns = useMemo(() => [
    { key: 'date', label: 'Date' },
    { key: 'user', label: 'User' },
    { key: 'topic', label: 'Topic' },
    { key: 'status', label: 'Status', align: 'center' },
    { key: 'actions', label: 'Actions', align: 'center' }
  ], []);

  const renderCell = useCallback((item, col) => {
    if (col.key === 'date') {
      const raw = item.date;
      if (!raw) return 'N/A';
      try {
        const dt = new Date(raw);
        if (isNaN(dt.getTime())) return String(raw);
        return dt.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
      } catch (e) {
        return String(raw);
      }
    }
    if (col.key === 'user') return (
      <Text size="sm" weight="medium">{item.username || `${item.first_name || ''} ${item.last_name || ''}`.trim() || 'N/A'}</Text>
    );

    if (col.key === 'topic') {
      // topic is display-only per new UX
      return item.topic || '—';
    }

    if (col.key === 'status') {
      const st = item.status || 'pending';
      const colorMap = { approved: '#2e7d32', rejected: '#c62828', pending: '#f9a825' };
      const Icon = st === 'approved' ? IoCheckmarkCircle : st === 'rejected' ? IoCloseCircle : IoCreateOutline;
      return (
        <Badge style={{ background: `${colorMap[st] || '#999'}20`, color: colorMap[st] || '#666' }}>
          <Icon size={14} />
          <span style={{ textTransform: 'capitalize' }}>{st}</span>
        </Badge>
      );
    }

    if (col.key === 'actions') {
      // Approve / Reject only; disable if already decided
      const decided = item.status === 'approved' || item.status === 'rejected';
      return (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8 }}>
          <Button variant="outline" disabled={decided} onClick={() => onUpdate(item.correction_id, { status: 'approved' })}>Approve</Button>
          <Button variant="ghost" disabled={decided} onClick={() => onUpdate(item.correction_id, { status: 'rejected' })}>Reject</Button>
        </div>
      );
    }

    return 'N/A';
  }, [onUpdate]);

  // transform corrections into table rows with id
  const rows = corrections.map(c => ({ ...c, id: c.correction_id }));

  return (
    <Table
      columns={columns}
      data={rows}
      renderCell={renderCell}
      getRowStatus={(item) => item.status}
      hoverable={true}
    />
  );
}

export default CorrectionsTable;
